/**
 * 접힘 영역 — 드물게 쓰는 입력·참고 정보를 한 줄 뒤로 접는다(점진적 공개).
 *
 * **펼친 채로 시작한다**(2026-10-15 사용자 지시 — 접힌 채로 시작하면 무엇이 있는지 몰라 찾아 헤맸다). 필요 없는
 * 사람은 한 번 눌러 접는다. 접으면 머리에 지금 값을 적는다(`summary`) — "우선순위 0 · 기본" 처럼. 열지 않고도
 * 무엇이 정해져 있는지 알아야 접기가 숨기기가 되지 않는다. `defaultOpen={false}` 는 접어 둘 까닭이 따로 있을 때만.
 *
 * `<details>` 대신 단추를 쓰는 것은 `Panel` 과 같은 까닭이다 — 오른쪽에 단추가 올 수 있고, 열림을
 * React 상태로 들고 있어야 안의 입력이 다시 그려질 때 닫히지 않는다.
 */
import { ChevronRight } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

export function Disclosure({
  title,
  summary,
  defaultOpen = true,
  forceOpen = false,
  className,
  children,
}: {
  /** 접힌 머리의 이름(예: "고급 설정", "기기 정보"). */
  title: string;
  /** 접혀 있을 때 함께 보이는 지금 값 한 줄. 펼치면 감춘다 — 안에 같은 것이 보인다. */
  summary?: ReactNode;
  defaultOpen?: boolean;
  /** 접혀 있어도 펼쳐 둔다 — 안의 칸에 저장을 막는 문제가 있어 그 칸으로 데려가야 할 때. */
  forceOpen?: boolean;
  className?: string;
  children: ReactNode;
}): ReactNode {
  const id = useId();
  const [openState, setOpen] = useState(defaultOpen);
  const open = openState || forceOpen;
  return (
    <div className={`disclosure${open ? ' is-open' : ''}${className ? ` ${className}` : ''}`}>
      <button
        type="button"
        className="disclosure__head"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((value) => !value)}
      >
        <ChevronRight className="disclosure__chevron" strokeWidth={2.25} aria-hidden="true" />
        <span className="disclosure__title">{title}</span>
        {!open && summary ? <span className="disclosure__summary">{summary}</span> : null}
      </button>
      {open ? (
        <div id={id} className="disclosure__body">
          {children}
        </div>
      ) : null}
    </div>
  );
}
